<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { getSystemVaultRoot, VaultKind, type VaultDirectoryInDb } from '@/api';
import FileVault from '@/components/file_vault/FileVault.vue';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import { extractErrorDetail } from '@/utils/errorHandler';

/**
 * The organisation's system vault (org admins only): the platform-managed
 * Templates and Intake Agent folders, split out of the main vault.
 * Everything below the root is browsed with the ordinary uid endpoints, so this
 * is the embedded FileVault rooted at the system root. Nothing can be created
 * or moved in here; content can only be moved out into the main vault.
 */
const root = ref<VaultDirectoryInDb | null>(null);
const loading = ref(true);
const errorMessage = ref('');

async function loadRoot(): Promise<void> {
  loading.value = true;
  errorMessage.value = '';
  try {
    const res = await getSystemVaultRoot();
    if (!res.data) throw new Error('Failed to load the system files.');
    root.value = res.data;
  } catch (error) {
    errorMessage.value = extractErrorDetail(error, 'Failed to load the system files.');
  } finally {
    loading.value = false;
  }
}

onMounted(loadRoot);
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.organisation }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.systemFiles }}</n-breadcrumb-item>
    </template>

    <n-flex v-if="loading" justify="center" :size="0" :wrap="false" :style="{ padding: '80px 0' }">
      <gaia-spinner />
    </n-flex>

    <gaia-empty-state v-else-if="errorMessage || !root" title="Couldn't load the system files">
      {{ errorMessage }}
      <template #actions>
        <gaia-button size="small" type="primary" @click="loadRoot">Retry</gaia-button>
      </template>
    </gaia-empty-state>

    <FileVault
      v-else
      :vault="VaultKind.SYSTEM"
      :root-directory-uid="root.uid"
      :root-label="navLabels.systemFiles"
      :show-upload="false"
      :show-create-folder="false"
    >
      <template #title>
        <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
          {{ navLabels.systemFiles }}
        </gaia-header>
      </template>
    </FileVault>
  </Layout>
</template>
